// src/utils/uploadRouter.ts

import { parentCodeForFileNumber, resolveCodeOnDemand, resolveOrCreateCodeOnDemand } from "./folderSetup";
import { uploadFileToParentItem } from "./sharepointupload";
import type { IndexRow, VDRProviderId } from "../vdr/providers";
import { requestListCodesForFile, requestListFolderPath } from "../vdr/requestlist";

export type RouteTarget = { parentCode: string | null; targetItemId: string; label: string };

export type RouteDecision = {
  code: string | null;
  parentCode: string | null;
  targetItemId: string;
  /** Set when one file goes to several folders (request-list VDRs); includes the first target. */
  allTargets?: RouteTarget[];
};

export type RoutedUploadResult = {
  fileName: string;
  status: "done" | "error" | "skipped";
  message?: string;
  webUrl?: string;
  /** Drive id of the SharePoint location the file ended up at (folder's drive). */
  driveId?: string;
  /** SharePoint item id of the uploaded file itself (not its parent folder). */
  itemId?: string;
  fileSize?: number;
  parentCode?: string | null;
  parentDriveId?: string;
  parentItemId?: string;
  parentPath?: string;
};

/** Extract code like "1.2.3" from "1.2.3 Contract.pdf" or "1.2.3_Contract.pdf" */
export function extractLeadingCode(fileName: string): string | null {
  const m = fileName.match(/^\s*(\d+(?:\.\d+)*)/);
  return m ? m[1] : null;
}

/**
 * Decide which SharePoint folder itemId a given filename should go to,
 * based ONLY on its leading digits (code) and the indexMap.
 *
 * - If no leading code => goes to rootItemId.
 * - If code has no "." => treated as top-level, goes to rootItemId.
 * - If code has "." => we get its parent code (e.g. "1.2.3" -> "1.2"),
 *   then look up that parent in indexMap to get a target folder itemId.
 *
 * For Firmex provider: uses relative-to-absolute code mapping from index rows.
 */
export function decideTargetForFilename(
  fileName: string,
  rootItemId: string,
  indexMap: Record<string, string>,
  providerId?: VDRProviderId,
  indexRows?: IndexRow[],
  file?: File
): RouteDecision | { error: string } {
  console.log(`   🎯 ROUTE: provider=${providerId} hasFile=${!!file} hasZipDir=${!!(file as any)?.__zipDir} file="${fileName}"`);

  // Firmex routing. A ZIP upload carries each file's folder path (__zipDir), whose
  // numbering matches the synced folder map — so we route by the folder the file
  // already sits in, never by its (often truncated/duplicated) filename. Files with
  // no __zipDir (individual uploads) fall to name matching. No silent fallback
  // between the two: a ZIP file that can't be placed hard-fails.
  if (providerId === "firmex") {
    const zipDir: string | undefined = (file as any)?.__zipDir;

    if (zipDir) {
      const { resolveFirmexFolderByZipPath } = require("./folderSetupFirmex");
      const code = resolveFirmexFolderByZipPath(zipDir, indexMap, indexRows);
      if (code && indexMap[code]) {
        console.log(`   ✅ Firmex ZIP: Routed "${fileName}" → folder "${code}"`);
        return { code: null, parentCode: code, targetItemId: indexMap[code] };
      }
      return { error: `Firmex ZIP: folder "${zipDir}" isn't in SharePoint yet — re-run folder creation to generate it, then re-sync and upload again.` };
    }

    if (indexRows) {
      const { resolveFirmexFileByPath } = require("./folderSetupFirmex");
      const folderCode = resolveFirmexFileByPath(fileName, indexRows);
      if (folderCode) {
        const targetItemId = indexMap[folderCode];
        if (targetItemId) {
          console.log(`   ✅ Firmex: Routed "${fileName}" to folder code "${folderCode}"`);
          return { code: null, parentCode: folderCode, targetItemId };
        }
        return { error: `Firmex: Folder code "${folderCode}" not found in folder index for "${fileName}"` };
      }
      return { error: `Firmex: No matching document row found in index for "${fileName}"` };
    }

    return { error: `Firmex: No index rows available to route "${fileName}"` };
  }

  // Datasite fileroom routing — only when index has 2+ filerooms.
  // A single fileroom means folders live at root; use standard routing instead.
  if (providerId === "dataroom" && indexRows && indexRows.filter(r => r.meta?.isFileRoom).length > 1) {
    const zipDir: string | undefined = (file as any)?.__zipDir;

    if (zipDir) {
      const segments = zipDir.split("/").filter(Boolean);
      // Datasite bulk downloads prefix FILEROOM folder names with a number and underscore
      // (e.g. "2_Phase I"). Strip that prefix before matching against the index title.
      const firstSegStripped = segments[0].replace(/^\d+(?:\.\d+)*_/, "").trim() || segments[0];
      const fileroomRow = indexRows.find(r =>
        r.meta?.isFileRoom && r.title.toLowerCase() === firstSegStripped.toLowerCase()
      );
      if (fileroomRow) {
        if (segments.length === 1) {
          const id = indexMap[`__fr:${fileroomRow.title}`];
          if (id) return { code: null, parentCode: null, targetItemId: id };
        } else {
          const deepest = segments[segments.length - 1];
          const m = deepest.match(/^\s*(\d+(?:\.\d+)*)/);
          const segCode = m ? m[1] : null;
          if (segCode) {
            const key = `__fr:${fileroomRow.title}::${segCode}`;
            if (indexMap[key]) return { code: null, parentCode: key, targetItemId: indexMap[key] };
          }
        }
        return { error: `Datasite: cannot route "${fileName}" in fileroom "${fileroomRow.title}"` };
      }
      return { error: `Datasite: fileroom not found for ZIP path "${zipDir}"` };
    }

    // Individual file routing: match by leading code + rootFolderName
    const code = extractLeadingCode(fileName);
    if (code && code.includes(".")) {
      const parentCode = code.slice(0, code.lastIndexOf("."));
      const docRow = indexRows.find(r => r.code === code && r.meta?.isDocument && r.meta?.rootFolderName);
      if (docRow) {
        const frName = docRow.meta!.rootFolderName as string;
        const key = `__fr:${frName}::${parentCode}`;
        if (indexMap[key]) return { code, parentCode: key, targetItemId: indexMap[key] };
        return { error: `Datasite: folder "${parentCode}" not found in fileroom "${frName}"` };
      }
    }
    return { error: `Datasite: cannot route "${fileName}" — no fileroom match found` };
  }

  // Request-list VDRs: files carry no codes. The index's Documents column lists the
  // requests a file is filed under — often several — and it goes to every one.
  if (providerId === "requestlist") {
    const rows = indexRows ?? [];
    const codes = requestListCodesForFile(fileName, rows);
    if (codes.length === 0) {
      return { error: `"${fileName}" isn't listed in the index — download a fresh index from the VDR and upload it again.` };
    }
    const missing = codes.filter((c) => !indexMap[c]);
    if (missing.length > 0) {
      return { error: `${missing.length} of this file's ${codes.length} folder(s) aren't in SharePoint yet — create folders from the index first.` };
    }
    const allTargets = codes.map((c) => ({ parentCode: c, targetItemId: indexMap[c], label: requestListFolderPath(c, rows) }));
    return { code: null, parentCode: codes[0], targetItemId: indexMap[codes[0]], allTargets };
  }

  // Standard number-based routing (non-Firmex providers)
  const code = extractLeadingCode(fileName);
  if (!code) {
    return { code: null, parentCode: null, targetItemId: rootItemId };
  }

  if (!code.includes(".")) {
    return { code, parentCode: null, targetItemId: rootItemId };
  }

  const parent = parentCodeForFileNumber(code);

  if (!parent) {
    return { code, parentCode: null, targetItemId: rootItemId };
  }

  const target = indexMap[parent];
  if (!target) {
    return {
      error: `Parent code not found in index: "${parent}" (from "${code}")`,
    };
  }

  return { code, parentCode: parent, targetItemId: target };
}

/* ----------------- MAIN UPLOAD FUNCTION ----------------- */

// How many uploads to run in parallel.
const CONCURRENCY = 3;
const DELAY_BETWEEN_UPLOADS_MS = 500; // Brief pause between uploads to avoid 429s
const UPLOAD_MAX_RETRIES = 3;

type UploadJob = {
  file: File;
  index: number; // index in the original files[] array
};

export async function routeAndUploadFiles(params: {
  driveId: string;
  rootItemId: string;
  files: File[];
  indexMap: Record<string, string>;
  providerId?: VDRProviderId;
  indexRows?: IndexRow[];
  onProgress?: (file: File, fraction: number) => void;
  onStatus?: (file: File, status: any, note?: string) => void;
}): Promise<RoutedUploadResult[]> {
  const { driveId, rootItemId, files, indexMap, providerId, indexRows, onProgress, onStatus } = params;

  // We'll store results by original index so final output preserves input order.
  // A slot holds one result per target folder (several for request-list VDRs).
  const resultsByIndex: (RoutedUploadResult[] | undefined)[] = new Array(
    files.length
  );

  const jobs: UploadJob[] = [];

  // 1) Queue all files for upload
  //    (ZIPs are pre-expanded by expandZipFiles before reaching here)
  files.forEach((file, index) => {
    onStatus?.(file, "uploading", "Preparing…");
    jobs.push({ file, index });
  });

  // 2) Function to process a single upload job (same logic as before, per-file).
  async function runJob(job: UploadJob): Promise<void> {
    const { file, index } = job;

    let decision = decideTargetForFilename(file.name, rootItemId, indexMap, providerId, indexRows, file);

    // If parent not in map, try to find it in SharePoint on-demand (code-based providers only)
    if ("error" in decision && providerId !== "firmex" && providerId !== "requestlist") {
      const code = extractLeadingCode(file.name);
      const parent = code?.includes(".") ? code.slice(0, code.lastIndexOf(".")) : null;
      if (parent) {
        console.log(`   🔍 Resolving missing parent "${parent}" for "${file.name}"...`);
        const resolved = await resolveCodeOnDemand(parent, driveId, rootItemId, indexMap);
        if (resolved) {
          decision = decideTargetForFilename(file.name, rootItemId, indexMap, providerId, indexRows, file);
        } else if (indexRows?.length) {
          // Folder doesn't exist in SharePoint — create it (and any missing ancestors)
          console.log(`   🔧 Creating missing parent folder "${parent}" for "${file.name}"...`);
          const created = await resolveOrCreateCodeOnDemand(parent, driveId, rootItemId, indexMap, indexRows);
          if (created) {
            decision = decideTargetForFilename(file.name, rootItemId, indexMap, providerId, indexRows, file);
          }
        }
      }
    }

    if ("error" in decision) {
      onStatus?.(file, "error", decision.error);
      resultsByIndex[index] = [{
        fileName: file.name,
        status: "error",
        message: decision.error,
      }];
      return;
    }

    const targets: RouteTarget[] = decision.allTargets ?? [
      { parentCode: decision.parentCode, targetItemId: decision.targetItemId, label: decision.parentCode ?? "root" },
    ];
    const multi = targets.length > 1; // per-folder statuses would flicker — report one summary at the end
    const results: RoutedUploadResult[] = [];

    for (let t = 0; t < targets.length; t++) {
      const target = targets[t];
      const folderId = target.targetItemId;

      for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
        try {
          const uploadResult = await uploadFileToParentItem(
            driveId,
            folderId,
            file,
            (fraction) => onProgress?.(file, (t + fraction) / targets.length),
            providerId,
            rootItemId,
            providerId !== "requestlist" // request-list folder names must stay exact
          );

          // uploadFileToParentItem returns either:
          //  - a "skipped" object (duplicate), now carrying refs to the EXISTING
          //    SharePoint file alongside the skipped flag, or
          //  - the Graph driveItem (no explicit success flag).
          if (uploadResult && uploadResult.skipped) {
            const msg = uploadResult.message ?? "Skipped";
            if (!multi) onStatus?.(file, "skipped", msg);
            // Surface the existing file's refs so the FDD queue (and any
            // other downstream consumer) can treat a skipped upload exactly
            // like a successful one — the file IS in SharePoint, we just
            // didn't put it there this round.
            const webUrl = typeof uploadResult.webUrl === "string" ? uploadResult.webUrl : undefined;
            const itemId = uploadResult.id ? String(uploadResult.id) : undefined;
            const itemDriveId = uploadResult.parentReference?.driveId
              ? String(uploadResult.parentReference.driveId)
              : driveId;
            const fileSize = typeof uploadResult.size === "number" ? uploadResult.size : file.size;
            results.push({
              fileName: file.name,
              status: "skipped",
              message: msg,
              webUrl,
              driveId: itemDriveId,
              itemId,
              fileSize,
              parentCode: target.parentCode,
              parentDriveId: driveId,
              parentItemId: folderId,
              parentPath: target.label,
            });
          } else {
            // Treat any non-error return as a success.
            const webUrl =
              typeof uploadResult === "object" && uploadResult?.webUrl
                ? uploadResult.webUrl
                : undefined;
            const itemId =
              typeof uploadResult === "object" && uploadResult?.id
                ? String(uploadResult.id)
                : undefined;
            const itemDriveId =
              typeof uploadResult === "object" && uploadResult?.parentReference?.driveId
                ? String(uploadResult.parentReference.driveId)
                : driveId;
            const fileSize =
              typeof uploadResult === "object" && typeof uploadResult?.size === "number"
                ? uploadResult.size
                : file.size;

            if (!multi) onStatus?.(file, "done", "Uploaded");
            results.push({
              fileName: file.name,
              status: "done",
              webUrl,
              driveId: itemDriveId,
              itemId,
              fileSize,
              parentCode: target.parentCode,
              parentDriveId: driveId,
              parentItemId: folderId,
              parentPath: target.label,
            });
          }
          break; // success — exit retry loop
        } catch (err: any) {
          const msg = err?.message ?? "Upload failed";
          const is429 = msg.includes("429") || msg.includes("Too Many Requests");
          const isRetryable = is429 || msg.includes("503") || msg.includes("504");

          if (isRetryable && attempt < UPLOAD_MAX_RETRIES) {
            const delay = is429 ? 5000 * attempt : 2000 * attempt;
            console.warn(`   ⚠️ Upload "${file.name}" got ${is429 ? "429" : "error"}, retrying in ${delay}ms (attempt ${attempt}/${UPLOAD_MAX_RETRIES})...`);
            onStatus?.(file, "uploading", `Throttled, retrying (${attempt}/${UPLOAD_MAX_RETRIES})…`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }

          if (!multi) onStatus?.(file, "error", msg);
          results.push({
            fileName: file.name,
            status: "error",
            message: msg,
          });
          break;
        }
      }
    }

    if (multi) {
      const uploaded = results.filter((r) => r.status === "done").length;
      const already = results.filter((r) => r.status === "skipped").length;
      const failed = results.filter((r) => r.status === "error");
      const summary = `${targets.length} folders: ${uploaded} uploaded, ${already} already there` +
        (failed.length ? `, ${failed.length} failed` : "");
      onStatus?.(
        file,
        failed.length ? "error" : uploaded ? "done" : "skipped",
        failed.length ? `${failed[0].message} (${summary})` : summary
      );
    }
    resultsByIndex[index] = results;
  }

  // 3) Simple concurrency pool: run up to CONCURRENCY jobs at once.
  const queue = [...jobs];

  const worker = async () => {
    while (true) {
      const job = queue.shift();
      if (!job) break;
      await runJob(job);
      // Brief pause between uploads to avoid throttling
      if (queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_UPLOADS_MS));
      }
    }
  };

  const workerCount = Math.min(CONCURRENCY, queue.length || 0) || 1;
  const workers = Array.from({ length: workerCount }, () => worker());

  await Promise.all(workers);

  // 4) Flatten results in original file order, dropping any undefined slots
  //    (there shouldn't be any, but this keeps types happy).
  const finalResults: RoutedUploadResult[] = resultsByIndex.flatMap((r) => r ?? []);

  return finalResults;
}
